import React from 'react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import ProductPhotoSpec from '../components/ProductPhotoSpec';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import AeoCallout from '../components/AeoCallout';
import { HERO_MEDIA, PRODUCT_MEDIA } from '../config/media';
import { RIG_MATS_HUB, RIG_MAT_SPEC, RIG_FAQS } from '../data/copy';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    productJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
} from '../lib/seo';

const RIG_HERO_FALLBACK = {
    hero: {
        eyebrow: RIG_MATS_HUB.eyebrow,
        headline: RIG_MATS_HUB.title,
        body: 'Heavy-duty multi-layer timber. Standard 8 ft widths, lengths to 40 ft, custom sizing on request.',
        image_url: HERO_MEDIA.snowField.src,
    },
};

export default function RigMats() {
    const content = usePageContent('products.rig-mats', RIG_HERO_FALLBACK);
    const hero = content.hero || RIG_HERO_FALLBACK.hero;
    return (
        <>
            <SEO
                title="Rig Mats - Drilling, Workover & Frac Spreads"
                description="Centurion rig mats - heavy-duty timber decks for drilling pads, workover rigs, and frac spreads. 8 ft × 14 ft, 8 ft × 30 ft, 8 ft × 40 ft and custom sizes."
                cmsSlug="seo.rig-mats"
                path="/rig-mats"
                image={HERO_MEDIA.snowField.src}
                jsonLd={[
                    orgJsonLd(),
                    productJsonLd({
                        name: 'Centurion Rig Mats',
                        description:
                            'Heavy-duty multi-layer timber rig mats for drilling pads, workover rigs and frac spreads.',
                        slug: '/rig-mats',
                        material: 'Coastal Hem-Fir / Coastal Fir, #2 & BTR structural grade',
                        additionalProperty: [
                            { name: 'Width', value: 8, unitText: 'FT' },
                            { name: 'Length', minValue: 14, maxValue: 40, unitText: 'FT' },
                            { name: 'Thickness', minValue: 8, maxValue: 14, unitText: 'IN' },
                            { name: 'Build', value: 'Multi-layer cross-laminated, bolted' },
                            { name: 'Configuration', value: 'Standard sizes 8 ft × 14 ft, 8 ft × 30 ft, 8 ft × 40 ft; custom on request' },
                        ],
                    }),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Rig Mats', path: '/rig-mats' },
                    ]),
                    faqJsonLd(RIG_FAQS),
                ]}
            />

            <HeroPlaceholder
                mediaKey="snowField"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            <ProductPhotoSpec
                testId="rig-mat-photo-spec"
                photo={{
                    src: PRODUCT_MEDIA.rigMat.src,
                    alt: PRODUCT_MEDIA.rigMat.alt,
                    caption: 'Heavy-timber rig mat - engineered for crane and drilling pads.',
                }}
                specTitle="Standard Centurion rig mat."
                specBody="Built for stationary heavy equipment over long campaigns. Engineered values for a specific job are signed off by your project engineer."
                specRows={RIG_MAT_SPEC}
                specTableTitle="Rig Mat - Standard"
                imageObjectPosition="top"
            />

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Stiffer, heavier, longer.
                </h2>
                <div className="mt-8 space-y-6 font-sans text-base leading-relaxed text-brand-muted">
                    {RIG_MATS_HUB.intro.map((p) => (
                        <p key={p.slice(0, 60)}>{p}</p>
                    ))}
                </div>
                <div className="mt-10 grid gap-4 md:grid-cols-2">
                    <AeoCallout
                        testId="aeo-rig-vs-access"
                        q="What's the difference between a rig mat and an access mat?"
                        a="An access mat protects ground and spreads load for moving traffic - trucks, low-beds, equipment crossings. A rig mat is a heavier, stiffer multi-layer timber deck built to sit under stationary heavy equipment for an entire campaign - drilling rigs, workover rigs, frac spreads, and lay-down yards."
                    />
                    <AeoCallout
                        testId="aeo-rig-sizes"
                        q="What sizes do Centurion rig mats come in?"
                        a="Standard Centurion rig mats are 8 ft × 14 ft, 8 ft × 30 ft and 8 ft × 40 ft. Custom widths and lengths are milled in Chemainus, BC to match your rig footprint and pad layout."
                    />
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
                <div className="grid gap-10 md:grid-cols-[1fr_1.4fr] md:gap-16">
                    <div>
                        <span className="eyebrow">Common Applications</span>
                        <h3 className="h-display mt-6 text-2xl sm:text-3xl">
                            Where Centurion rig mats live.
                        </h3>
                    </div>
                    <ul className="grid gap-3 sm:grid-cols-2">
                        {RIG_MATS_HUB.applications.map((a) => (
                            <li
                                key={a}
                                className="flex items-start gap-3 border border-brand-hairline bg-brand-surface p-4 font-sans text-sm text-brand-text"
                            >
                                <span
                                    aria-hidden="true"
                                    className="mt-1.5 h-1.5 w-1.5 flex-none bg-brand-gold"
                                />
                                {a}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">FAQ</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Common questions about rig mats.
                </h2>
                <div className="mt-10">
                    <FaqAccordion faqs={RIG_FAQS} />
                </div>
            </section>

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Spec a rig mat package."
                body="Send us your campaign duration, rig footprint and access route. We’ll quote Manufacturer Direct."
                cta={{ to: '/quote', label: 'Request a Spec Sheet' }}
            />
        </>
    );
}
